import { Injectable } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { NzButtonType } from 'ng-zorro-antd/button';
import { NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Injectable({ providedIn: 'root' })
export class UtilsService {
    constructor(
        private translateService: TranslateService,
        private notificationService: NzNotificationService,
        private modal: NzModalService
    ) { }

    public createErrorNotification(title: string, errorMessage: string): void {
        const options = {};
        this.notificationService.error(title, errorMessage, options);
    }

    public createSuccessNotification(
        title: string,
        successMessage: string
    ): void {
        const options = {};
        this.notificationService.success(title, successMessage, options);
    }

    /**
     * Download file from blob
     */
    public saveFile(blob: Blob, fileName: string) {
        const a = document.createElement('a');
        document.body.appendChild(a);
        const url = window.URL.createObjectURL(blob);
        a.href = url;
        a.download = fileName;
        a.click();
        setTimeout(() => {
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        }, 0);
    }

    public isVietnamesePhoneNumber(phone: string) {
        return /(03|07|08|09|01[2|6|8|9])+([0-9]{8})\b/.test(phone);
    }

    public showConfirmModal({
        title = '',
        content = '',
        okText = 'OK',
        okButtonType = 'danger',
        onOk = () => { },
        cancelText = this.translateService.instant('common.cancel'),
        onCancel = () => { }
    }) {
        this.modal.confirm({
            nzTitle: title,
            nzContent: content,
            nzOkText: okText,
            nzOkType: okButtonType as NzButtonType,
            nzOnOk: onOk,
            nzCancelText: cancelText,
            nzOnCancel: onCancel
        });
    }

    getBase64(img: File | Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader: any = new FileReader();
            reader.addEventListener('load', () =>
                resolve(reader.result?.toString())
            );
            reader.readAsDataURL(img);
        });
    }

    getRaw(img: File | Blob): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader: any = new FileReader();
            reader.addEventListener('load', () =>
                resolve(reader.result?.toString())
            );
            reader.readAsText(img);
        });
    }

    startOfDate(date: string | Date) {
        const m = moment(date)
            .startOf('day')
            .toDate();
        return m;
    }

    endOfDate(date: string | Date) {
        const m = moment(date)
            .endOf('day')
            .toDate();
        return m;
    }

    getUtcDate(date?: string | Date) {
        if (!date) {
            date = new Date();
        }
        return moment(date)
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss');
    }

    getDateFromUtc(utcDate?: string | Date) {
        return moment.utc(utcDate).toDate();
    }

    getStartOfDate(date: string | Date) {
        return moment(date)
            .startOf('day')
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss');
    }

    getEndOfDate(date: string | Date) {
        return moment(date)
            .endOf('day')
            .utc()
            .format('YYYY-MM-DDTHH:mm:ss');
    }

    combineDatetime(date: string | Date, datetime: string | Date) {
        const time = moment(datetime);
        const combined = moment(date);
        combined
            .hours(time.hour())
            .minutes(time.minute())
            .seconds(time.second());
        return combined.toDate();
    }

    removeVietnameseTones(str: string): string {
        const newStr = str
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/đ/g, 'd')
            .replace(/Đ/g, 'D');
        return newStr;
    }

    getTinyEditorConfig() {
        const tinyMCEConfig = {
            base_url: '/tinymce', // Root for resources
            suffix: '.min', // Suffix to use when loading resources
            height: 500,
            menubar: false,
            plugins: [
                'advlist autolink lists link image charmap print preview anchor',
                'searchreplace visualblocks fullscreen',
                'insertdatetime media table paste code help wordcount image paste link'
            ],
            toolbar:
                'undo redo | formatselect | bold italic backcolor | \
                    alignleft aligncenter alignright alignjustify | \
                    bullist numlist outdent indent | removeformat |image| link | code | help'
        };
        return tinyMCEConfig;
    }

    makeOptionsListFromEnum = (
        enumType: any,
        context: string
    ): Array<{ label: string; value: any }> =>
        Object.keys(enumType)
            .filter((key: any) => !(parseInt(key, 10) > -1))
            .map((key: any) => {
                const value = enumType[key];
                return {
                    label: this.translateService.instant(`${context}.${key}`),
                    value: value
                };
            });

    makeOptionsListFromEnumValue = (
        enumType: any,
        context: string
    ): Array<{ label: string; value: any }> =>
        Object.values(enumType)
            .filter((key: any) => !(parseInt(key, 10) > -1))
            .map((key: any) => ({
                label: this.translateService.instant(`${context}.${key}`),
                value: key
            }));
    s4() {
        return Math.floor((1 + Math.random()) * 0x10000)
            .toString(16)
            .substring(1);
    }

    generateGuid() {
        return (
            this.s4() +
            this.s4() +
            '-' +
            this.s4() +
            '-' +
            this.s4() +
            '-' +
            this.s4() +
            '-' +
            this.s4() +
            this.s4() +
            this.s4()
        );
    }

    timeLeft(dateTime: Date) {
        const now = moment(new Date());
        const end = moment(dateTime);
        const duration = moment.duration(end.diff(now));
        const days = duration.asDays();
        const hours = duration.asHours();
        const minutes = duration.asMinutes();
        const seconds = duration.asSeconds();
        let value = 0;
        let unit = '';

        if (days >= 1) {
            value = days;
            unit = this.translateService.instant('common.days');
            if (days === 1) {
                unit = this.translateService.instant('common.day');
            }
        } else if (hours >= 1) {
            value = hours;
            unit = this.translateService.instant('common.hours');
            if (hours === 1) {
                unit = this.translateService.instant('common.hour');
            }
        } else if (minutes >= 1) {
            value = minutes;
            unit = this.translateService.instant('common.minutes');
            if (minutes === 1) {
                unit = this.translateService.instant('common.minute');
            }
        } else if (seconds >= 1) {
            value = seconds;
            unit = this.translateService.instant('common.seconds');
            if (seconds === 1) {
                unit = this.translateService.instant('common.second');
            }
        }
        return this.translateService.instant('common.time_left', {
            timeLeft: `${value.toFixed(0)} ${unit}`
        });
    }

    timeLeftSeconds(dateTime: Date | string) {
        const now = moment(new Date());
        const end = moment(dateTime);
        const duration = moment.duration(end.diff(now));
        const seconds = duration.asSeconds();
        return seconds;
    }
    pauseAllVideo(exclude: string) {
        const videos = document.querySelectorAll('video');
        for (let i = 0; i < videos.length; i++) {
            // Is this the one we want to play?
            if (videos[i].id === exclude) {
                continue;
            }
            // Have we already played it && is it already paused?
            if (videos[i].played.length > 0 && !videos[i].paused) {
                // Then pause it now
                videos[i].pause();
            }
        }
    }

    fileSizeToString(size: number, extension: string = 'MB') {
        let bytes = 1;
        let fixed = 0;

        switch (extension) {
            case 'MB':
                bytes = 1024 * 1024;
                fixed = 0;
        }
        return (size / bytes).toFixed(fixed) + extension;
    }

    msToTime(duration: number) {
        const seconds = Math.floor((duration / 1000) % 60),
            minutes = Math.floor((duration / (1000 * 60)) % 60),
            hours = Math.floor((duration / (1000 * 60 * 60)) % 24);

        // let hour = '',
        //     min = '',
        //     sec = '';
        const parts = [];

        if (hours >= 1) {
            parts.push(hours + ' h');
        }

        if (minutes >= 1) {
            parts.push(minutes + ' min');
        }

        if (seconds > 1 && parts.length <= 1 && minutes < 2) {
            parts.push(seconds + ' s');
        }

        return parts.join(' ');
    }

    generateEnrollNumber(id: string, total: number) {
        const numb = id?.replace(/(^.+)(\w\d+\w)(.+$)/i, '$2');
        const number = Number(numb[0]) || 5;
        return number * 552 + total;
    }

    generateLikeNumber(id: string, total: number) {
        const numb = id.replace(/(^.+)(\w\d+\w)(.+$)/i, '$2');
        const number = Number(numb[0]) || 5;
        return number * 390 + total;
    }

    formatterCurrency = (value: number) =>
        `${value?.toFixed(1)?.replace(/\d(?=(\d{3})+\.)/g, '$&,')} VND`;
    formatterCurrencyVND = (value: number) =>
        `${value?.toLocaleString('en-US')} VND`;

    urlValidation = (control: UntypedFormControl): { [s: string]: boolean } => {
        const value = control?.value;
        if (!value) {
            return {};
        }

        const pattern = new RegExp(
            '^(https?:\\/\\/)' + // protocol
            '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
            '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
            '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
            '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
            '(\\#[-a-z\\d_]*)?$',
            'i'
        ); // fragment locator

        const isValid = pattern.test(value);
        console.log('valid', isValid ? {} : { isValid: false });
        return isValid ? {} : { isValid: false };
    };

    public formatTreeSelectData(data: any[]) {
        return data.map(unit => {
            unit.title = unit.name;
            unit.key = unit.id;
            (unit.children || []).map(child => {
                child.key = child.id;
                child.title = child.name;
                child.isLeaf = true;
            });
            unit.isLeaf = unit.children?.length === 0;
            return unit;
        });
    }

    getDayName(day: number): string {
        const $dayOfWeek = [
            'day.monday',
            'day.tuesday',
            'day.wednesday',
            'day.thursday',
            'day.friday',
            'day.saturday',
            'day.sunday'
        ];
        return $dayOfWeek[day - 1];
    }

    getEnumKeyFromStringValue(value: string, EnumObject: Object) {
        return Object.entries(EnumObject).find(
            ([key, val]) => val === value
        )?.[0];
    }
}
